import React, { useState } from 'react';
import './ManageSettlements.css';
import { Alert, Button, Card, Checkbox, Col, DatePicker, Input, Row, Table, Typography, message } from 'antd';
import { SaveOutlined, SearchOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import {
  searchSettlementsByRequest,
  SupportSettlement,
  updateSettlementFields,
} from '../../../services/supportSuite';

interface SettlementEdition {
  fecha_liquidacion?: string;
  fecha_vencimiento?: string;
  referencia_municipal?: string;
}

const ManageSettlements: React.FC = () => {
  const [idSolicitud, setIdSolicitud] = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState<{ [id: number]: boolean }>({});
  const [liquidaciones, setLiquidaciones] = useState<SupportSettlement[]>([]);
  const [editing, setEditing] = useState<{ [id: number]: SettlementEdition & { desvincular?: boolean } }>({});

  const handleSearch = async () => {
    if (!idSolicitud || !/^\d+$/.test(idSolicitud)) {
      message.warning('Debe ingresar un identificador de solicitud valido');
      return;
    }

    setSearching(true);
    try {
      const response = await searchSettlementsByRequest(Number(idSolicitud));
      const data = response?.liquidaciones || [];
      setLiquidaciones(data);
      setEditing({});

      if (!data.length) {
        message.info('No se encontraron liquidaciones para la solicitud indicada');
      }
    } catch (error) {
      const apiError = error as any;
      message.error(apiError?.response?.data?.message || 'Error al consultar liquidaciones');
      setLiquidaciones([]);
    } finally {
      setSearching(false);
    }
  };

  const updateField = (id: number, field: keyof (SettlementEdition & { desvincular?: boolean }), value: string | boolean) => {
    setEditing((previous) => ({
      ...previous,
      [id]: {
        ...previous[id],
        [field]: value,
      },
    }));
  };

  const getCurrentValue = <T extends keyof SettlementEdition>(record: SupportSettlement, key: T) => {
    if (editing[record.id_liquidacion] && editing[record.id_liquidacion][key] !== undefined) {
      return editing[record.id_liquidacion][key] as SettlementEdition[T];
    }

    if (key === 'fecha_liquidacion') return record.fecha_liquidacion as SettlementEdition[T];
    if (key === 'fecha_vencimiento') return record.fecha_vencimiento as SettlementEdition[T];
    return undefined;
  };

  const handleSave = (record: SupportSettlement) => async () => {
    const currentFechaLiquidacion = getCurrentValue(record, 'fecha_liquidacion') as string;
    const currentFechaVencimiento = getCurrentValue(record, 'fecha_vencimiento') as string;
    const referenciaMunicipal = (editing[record.id_liquidacion]?.referencia_municipal || '').trim();
    const desvincular = editing[record.id_liquidacion]?.desvincular;

    if (!currentFechaLiquidacion || !moment(currentFechaLiquidacion, 'YYYY-MM-DD', true).isValid()) {
      message.warning('La fecha de liquidacion es invalida');
      return;
    }

    if (!currentFechaVencimiento || !moment(currentFechaVencimiento, 'YYYY-MM-DD', true).isValid()) {
      message.warning('La fecha de vencimiento es invalida');
      return;
    }

    // Si desvincular está activo, no validar el RIM
    if (!desvincular && record.id_registro_municipal === null && referenciaMunicipal && referenciaMunicipal.length < 3) {
      message.warning('La referencia municipal (RIM) parece invalida');
      return;
    }

    setSaving((previous) => ({ ...previous, [record.id_liquidacion]: true }));
    try {
      const response = await updateSettlementFields({
        id_liquidacion: record.id_liquidacion,
        fecha_liquidacion: currentFechaLiquidacion,
        fecha_vencimiento: currentFechaVencimiento,
        referencia_municipal: desvincular ? '' : (record.id_registro_municipal === null && referenciaMunicipal ? referenciaMunicipal : undefined),
      });

      message.success(response?.message || 'Liquidacion actualizada correctamente');
      await handleSearch();
    } catch (error) {
      const apiError = error as any;
      message.error(apiError?.response?.data?.message || 'No se pudo actualizar la liquidacion');
    } finally {
      setSaving((previous) => ({ ...previous, [record.id_liquidacion]: false }));
    }
  };

  const renderDatePicker = (record: SupportSettlement, key: 'fecha_liquidacion' | 'fecha_vencimiento') => {
    const currentValue = getCurrentValue(record, key) as string;
    const momentValue: Moment | undefined = currentValue ? moment(currentValue) : undefined;

    return (
      <DatePicker
        value={momentValue}
        format='YYYY-MM-DD'
        onChange={(date) => updateField(record.id_liquidacion, key, date ? date.format('YYYY-MM-DD') : '')}
      />
    );
  };

  const columns = [
    {
      title: 'ID Liquidacion',
      dataIndex: 'id_liquidacion',
      key: 'id_liquidacion',
      width: 120,
    },
    {
      title: 'Fecha Liquidacion',
      key: 'fecha_liquidacion',
      render: (_: any, record: SupportSettlement) => renderDatePicker(record, 'fecha_liquidacion'),
      width: 180,
    },
    {
      title: 'Fecha Vencimiento',
      key: 'fecha_vencimiento',
      render: (_: any, record: SupportSettlement) => renderDatePicker(record, 'fecha_vencimiento'),
      width: 180,
    },
    {
      title: 'Registro Municipal',
      key: 'id_registro_municipal',
      render: (_: any, record: SupportSettlement) => {
        const desvincular = editing[record.id_liquidacion]?.desvincular;
        // Si tiene RIM asignado, mostrar campo editable + check para desvincular
        if (record.id_registro_municipal !== null) {
          const rimValue = record.referencia_municipal || record.id_registro_municipal;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Input
                placeholder='Ingrese el RIM para asociar'
                value={desvincular ? '' : (editing[record.id_liquidacion]?.referencia_municipal ?? rimValue ?? '')}
                onChange={(event) => updateField(record.id_liquidacion, 'referencia_municipal', event.target.value.toUpperCase())}
                disabled={desvincular}
                style={{ width: 120 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Checkbox
                  checked={!!desvincular}
                  onChange={e => updateField(record.id_liquidacion, 'desvincular', e.target.checked)}
                  style={{ color: 'red', fontWeight: 'bold', marginRight: 4 }}
                />
                <span style={{ color: 'red', fontWeight: 'bold', userSelect: 'none' }}>Desvincular</span>
              </div>
            </div>
          );
        }
        // Si no tiene RIM, campo normal
        return (
          <Input
            placeholder='Ingrese el RIM para asociar'
            value={editing[record.id_liquidacion]?.referencia_municipal || ''}
            onChange={(event) => updateField(record.id_liquidacion, 'referencia_municipal', event.target.value.toUpperCase())}
          />
        );
      },
      width: 260,
    },
    {
      title: 'Monto MMV',
      dataIndex: 'monto_petro',
      key: 'monto_petro',
      render: (value: number) => value,
      width: 120,
    },
    {
      title: 'Accion',
      key: 'accion',
      render: (_: any, record: SupportSettlement) => (
        <Button
          icon={<SaveOutlined />}
          type='primary'
          loading={Boolean(saving[record.id_liquidacion])}
          onClick={handleSave(record)}
        >
          Guardar
        </Button>
      ),
      width: 140,
      fixed: 'right' as 'right',
    },
  ];

  return (
  <Card bodyStyle={{ padding: 0 }}>
      <Typography.Paragraph>
        Gestiona fechas y la asociacion del RIM cuando la liquidacion todavia no tiene uno asignado.
      </Typography.Paragraph>

      <Alert
        showIcon
        type='info'
        message='Reglas de edición'
        description={
          <span>
            Para asociar registro municipal, ingrese el RIM.<br /><br />
            Puede desvincular una liquidación de su referencia municipal usando el check "Desvincular".<br /><br />
            <span style={{ background: 'yellow', color: '#222', fontWeight: 'bold', padding: '0 4px', borderRadius: 2 }}>
              Importante:
            </span>
            {' Respalde la referencia municipal por su cuenta antes de guardar, ya que el sistema no la almacena en ningún historial.'}
          </span>
        }
        style={{ marginBottom: 16 }}
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input
            placeholder='Identificador de solicitud'
            value={idSolicitud}
            onChange={(event) => setIdSolicitud(event.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button type='primary' icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
            Buscar liquidaciones
          </Button>
        </Col>
      </Row>

      <div className="settlements-table-scroll">
        <Table
          rowKey='id_liquidacion'
          dataSource={liquidaciones}
          columns={columns}
          pagination={false}
          locale={{ emptyText: 'Sin resultados' }}
          scroll={{ x: true }}
          style={{ minWidth: 900 }}
        />
      </div>
    </Card>
  );
};

export default ManageSettlements;
